import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  Divider,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Autorenew as AutorenewIcon
} from '@mui/icons-material';

const ProgressTracker = ({ 
  applicationData, 
  activePhase = 'application',
  showDetails = true,
  compact = false 
}) => {
  // 添加CSS动画样式
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // 阶段配置
  const phaseConfig = {
    preparation: { title: '准备', icon: '📋', color: 'info' },
    application: { title: '申请', icon: '📝', color: 'warning' },
    visa: { title: '签证', icon: '🛂', color: 'primary' },
    enrollment: { title: '入学', icon: '🎓', color: 'success' },
    completed: { title: '完成', icon: '✅', color: 'success' }
  };

  // 步骤状态样式配置
  const getStepStatusConfig = (status) => {
    switch (status) {
      case 'Pass':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
          iconColor: 'success',
          borderColor: 'success.main',
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)',
          textColor: 'success.main',
          label: ''
        };
      case 'In Progress':
        return {
          icon: <Box sx={{ 
            display: 'inline-flex',
            animation: 'spin 2s linear infinite',
            color: 'warning.main'
          }}>
            <AutorenewIcon sx={{ fontSize: 20 }} />
          </Box>,
          iconColor: 'warning',
          borderColor: 'warning.main',
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.08) 100%)',
          textColor: 'warning.main',
          label: ''
        };
      case 'Reject':
        return {
          icon: <CancelIcon sx={{ fontSize: 20 }} />,
          iconColor: 'error',
          borderColor: 'error.main',
          background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.08) 100%)',
          textColor: 'error.main',
          label: ''
        };
      case 'Pending':
        return {
          icon: <HourglassEmptyIcon sx={{ fontSize: 20 }} />,
          iconColor: 'info',
          borderColor: 'info.main',
          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.08) 100%)',
          textColor: 'info.main',
          label: ''
        };
      case 'NotYet':
        return {
          icon: <InfoIcon sx={{ fontSize: 20 }} />,
          iconColor: 'disabled',
          borderColor: 'grey.300',
          background: 'linear-gradient(135deg, rgba(158, 158, 158, 0.1) 0%, rgba(158, 158, 158, 0.05) 100%)',
          textColor: 'grey.600',
          label: ''
        };
      default:
        return {
          icon: <InfoIcon sx={{ fontSize: 20 }} />,
          iconColor: 'disabled',
          borderColor: 'grey.300',
          background: 'linear-gradient(135deg, rgba(158, 158, 158, 0.1) 0%, rgba(158, 158, 158, 0.05) 100%)',
          textColor: 'grey.600',
          label: ''
        };
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />;
      case 'in_progress':
        return <ScheduleIcon color="primary" sx={{ fontSize: 20 }} />;
      case 'pending':
        return <PendingIcon color="disabled" sx={{ fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon color="error" sx={{ fontSize: 20 }} />;
      default:
        return <InfoIcon color="disabled" sx={{ fontSize: 20 }} />;
    }
  };

  if (!applicationData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            暂无申请数据
          </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* 申请信息头部 */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            background: `linear-gradient(135deg, #e3f2fd 0%, #e8f5e8 100%)`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* 大学Logo */}
            <Box
              sx={{
                width: 120,
                height: 75,
                overflow: 'hidden',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {applicationData.universityLogo ? (
                <img
                  src={applicationData.universityLogo}
                  alt={applicationData.universityName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <Box
                sx={{
                  display: applicationData.universityLogo ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}
              >
                {applicationData.universityName?.charAt(0) || 'U'}
              </Box>
            </Box>
            
            {/* 学校和专业信息 */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h5" 
                fontWeight={700} 
                color="primary.main"
                sx={{ mb: 0.5 }}
              >
                {applicationData.universityFullName || applicationData.universityName}
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {applicationData.programName}
              </Typography>
            </Box>
            
            {/* 当前状态 */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                当前状态
        </Typography>
              <Chip 
                label={applicationData.statusInfo?.text || '未知'}
                color={applicationData.statusInfo?.color || 'default'}
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  height: 32
                }}
              />
            </Box>
          </Box>
        </Paper>
        
        <Box sx={{ width: '100%' }}>
        {applicationData.progressDetails && Object.keys(applicationData.progressDetails).map((phaseKey, phaseIndex) => {
          const phaseData = applicationData.progressDetails[phaseKey];
          const phaseInfo = phaseConfig[phaseKey];
          if (!phaseInfo || !phaseData) return null;
          
          // 计算阶段状态
          const steps = Object.values(phaseData);
          const completedSteps = steps.filter(step => step.completed);
          const phaseStatus = completedSteps.length === 0 ? 'pending' : 
                            completedSteps.length === steps.length ? 'completed' : 'in_progress';
            
          return (
            <Box key={phaseKey} sx={{ mb: 4 }}>
              {/* 阶段标题行 */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                {/* 阶段图标 */}
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: phaseStatus === 'completed' 
                          ? 'success.main' 
                        : phaseStatus === 'in_progress'
                          ? 'primary.main'
                          : 'grey.300',
                      color: 'white',
                      fontSize: '1.2rem'
                    }}
                  >
                    {phaseStatus === 'completed' ? '✓' : phaseInfo.icon}
                  </Box>
                  {phaseStatus === 'in_progress' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    </Box>
                  )}
                </Box>
                
                {/* 阶段标题和状态 */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    {phaseInfo.title}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={phaseStatus === 'completed' ? '已完成' : 
                             phaseStatus === 'in_progress' ? '进行中' : '待开始'}
                      color={phaseStatus === 'completed' ? 'success' : 
                             phaseStatus === 'in_progress' ? 'primary' : 'default'}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {completedSteps.length} / {steps.length} 完成
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
              
              {/* 步骤卡片 */}
              <Box sx={{ 
                display: 'grid',
                gap: 1,
                width: '100%',
                gridTemplateColumns: 'repeat(3, 1fr)'
              }}>
                {Object.entries(phaseData).map(([stepKey, stepData]) => {
                  const status = stepData.status || 'NotYet';
                  const statusConfig = getStepStatusConfig(status);
                  
                  return (
                    <Card
                      key={stepKey}
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: statusConfig.borderColor,
                        background: statusConfig.background,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      <CardContent sx={{ p: '8px !important', '&:last-child': { pb: '8px' } }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ color: statusConfig.textColor }}>
                            {statusConfig.icon}
                          </Box>
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            sx={{ 
                              color: statusConfig.textColor,
                              fontSize: '0.875rem'
                            }}
                          >
                            {stepData.name}
                          </Typography>
                        </Stack>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: statusConfig.textColor,
                            fontSize: '0.75rem',
                            opacity: 0.8,
                            display: 'block',
                            mt: 0.5
                          }}
                        >
                          {statusConfig.label}
                        </Typography>
                        
                        {/* 签证进度百分比显示 */}
                        {stepKey === 'visa_progress' && stepData.content && !isNaN(parseFloat(stepData.content)) && (
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: statusConfig.textColor }}>
                                进度
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: statusConfig.textColor }}>
                                {parseFloat(stepData.content)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={parseFloat(stepData.content)}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: statusConfig.textColor,
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          );
        })}
        </Box>
        </Box>
  );
};

export default ProgressTracker;